/**
 * GuaranteeWidget — Live 3× ROI tracking for the signed-in customer
 *
 * Sits on the Dashboard. Pulls /api/me/guarantee-status (real numbers, no mocks).
 * Honest when Stripe isn't live yet — says so plainly.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, AlertTriangle, TrendingUp, ArrowRight, Sparkles, Info,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  on_track: {
    color: 'emerald', icon: ShieldCheck, label: 'On track',
    desc: 'Realized savings are pacing ≥ pro-rated target.',
  },
  ahead: {
    color: 'emerald', icon: ShieldCheck, label: 'Ahead of target',
    desc: 'You\'re already past the 3× threshold.',
  },
  behind: {
    color: 'amber', icon: AlertTriangle, label: 'Behind',
    desc: 'Realized savings are short of pro-rated pace. Engage the Auto-Compiler cascade for a boost.',
  },
  no_subscription: {
    color: 'sky', icon: Info, label: 'No billing yet',
    desc: 'The 3× ROI clock starts the day your first invoice is paid. Auto-Compiler savings are tracked in the meantime.',
  },
};

const formatUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `$${(v / 1000).toFixed(2)}K`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(4)}`;
};

export default function GuaranteeWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/me/guarantee-status`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5 animate-pulse" data-testid="guarantee-widget-loading">
        <div className="h-4 w-32 bg-white/5 rounded mb-3"></div>
        <div className="h-8 w-48 bg-white/5 rounded mb-2"></div>
        <div className="h-3 w-64 bg-white/5 rounded"></div>
      </div>
    );
  }

  if (!data) return null;

  const meta = STATUS_META[data.status] || STATUS_META.no_subscription;
  const Icon = meta.icon;
  const ringColor = {
    emerald: 'border-emerald-500/30 bg-slate-900 bg-gradient-to-br from-emerald-500/5 via-slate-900 to-slate-900',
    amber:   'border-amber-500/40 bg-slate-900 bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-900',
    sky:     'border-sky-500/30 bg-slate-900 bg-gradient-to-br from-sky-500/5 via-slate-900 to-slate-900',
  }[meta.color];
  const textColor = {
    emerald: 'text-emerald-300',
    amber:   'text-amber-300',
    sky:     'text-sky-300',
  }[meta.color];

  // Compute progress bar (capped at 100% when ahead)
  const progressPct = data.subscription_paid_usd > 0
    ? Math.min(100, (data.savings_booked_usd / data.required_savings_usd) * 100)
    : (data.savings_booked_usd > 0 ? 25 : 0);

  return (
    <div className={`rounded-2xl border ${ringColor} p-5`} data-testid="guarantee-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${textColor}`} />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">3× ROI guarantee</div>
            <div className={`text-lg font-bold ${textColor}`} data-testid="guarantee-status-label">{meta.label}</div>
          </div>
        </div>
        <Link to="/guarantee" className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="guarantee-widget-policy-link">
          Read the policy <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{meta.desc}</p>

      {/* Numbers row */}
      <div className="mt-4 grid grid-cols-3 gap-3" data-testid="guarantee-widget-numbers">
        <Pair label="Paid to CARTIE" value={formatUsd(data.subscription_paid_usd)} testid="guarantee-paid" />
        <Pair label="Savings booked" value={formatUsd(data.savings_booked_usd)} testid="guarantee-booked" accent={meta.color} />
        <Pair
          label="Multiplier"
          value={data.multiplier === 'n/a' ? '—' : `${Number(data.multiplier).toFixed(2)}×`}
          testid="guarantee-multiplier"
          accent={meta.color}
        />
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 mb-1">
          <span>Progress to 3× threshold</span>
          <span data-testid="guarantee-days-remaining">{data.days_remaining}d remaining of {data.window_days}</span>
        </div>
        <div className="w-full h-1.5 bg-slate-900 rounded-full overflow-hidden" data-testid="guarantee-progress-bar">
          <div
            className={`h-full transition-all duration-500 ${
              meta.color === 'emerald' ? 'bg-emerald-500' :
              meta.color === 'amber' ? 'bg-amber-400' : 'bg-sky-400'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Honesty note when Stripe isn't live */}
      {!data.stripe_live && (
        <div className="mt-4 rounded-lg bg-slate-950/60 border border-white/5 px-3 py-2 text-[11px] text-slate-400 flex gap-2" data-testid="guarantee-honesty-note">
          <Info className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
          <span>{data.honesty_note}</span>
        </div>
      )}

      {data.breakdown?.engine_27_proxy_usd > 0 && (
        <div className="mt-3 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-emerald-400" />
          Auto-Compiler contribution: <span className="font-mono text-emerald-300">{formatUsd(data.breakdown.engine_27_proxy_usd)}</span>
        </div>
      )}
    </div>
  );
}

function Pair({ label, value, testid, accent }) {
  const textColor = {
    emerald: 'text-emerald-300',
    amber:   'text-amber-300',
    sky:     'text-sky-300',
  }[accent] || 'text-white';
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-lg font-extrabold mt-0.5 font-mono tabular-nums ${textColor}`}>{value}</div>
    </div>
  );
}
